import React, { useEffect, useState } from "react";
import { FileSignature, CheckCircle2, XCircle, Eye, RefreshCw, Send, Download, LayoutGrid, ListTree, FileArchive, ShieldCheck, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import MassnahmenKatalog from "@/components/MassnahmenKatalog";

const STATUS_LABEL = {
  pending_approval: "Offen zur Freigabe",
  sent_for_signature: "Unterschrift ausstehend",
  signed: "Unterschrieben",
  rejected: "Abgelehnt",
};
const STATUS_COLOR = {
  pending_approval: "bg-amber-100 text-amber-800",
  sent_for_signature: "bg-[#00CCC9]/20 text-[#0A6E6C]",
  signed: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
};

export default function VertraegePage() {
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [wbParticipants, setWbParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending_approval");
  const [mainTab, setMainTab] = useState("workflow");
  const [customerType, setCustomerType] = useState("avgs");
  const [selected, setSelected] = useState(null);
  const [manualClientId, setManualClientId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [j, c, p] = await Promise.all([
        api.get("/contract-jobs"),
        api.get("/clients"),
        api.get("/weiterbildung/participants"),
      ]);
      setJobs(j.data);
      setClients(c.data);
      setWbParticipants(p.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const manualTrigger = async () => {
    if (!manualClientId) { toast.error(customerType === "weiterbildung" ? "Bitte Teilnehmer wählen" : "Bitte Kunde wählen"); return; }
    try {
      const r = await api.post("/contract-jobs/manual-trigger", { client_id: manualClientId, customer_type: customerType });
      if (r.data.note) {
        toast.info(r.data.note + " — Daten wurden gerade mit dem aktuellen Akte-Stand aktualisiert.");
      } else {
        toast.success("Vertrag wird erzeugt");
      }
      setManualClientId("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const resetToApproval = async (job) => {
    if (!window.confirm(`Vertrag von "${job.client_name}" wirklich zurück auf "Offen zur Freigabe" setzen? Der bisherige Unterschrift-Link wird ungültig.`)) return;
    try {
      await api.post(`/contract-jobs/${job.job_id}/reset-to-approval`);
      toast.success("Vertrag zurückgesetzt — jetzt wieder unter \"Offen zur Freigabe\"");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const resendSignatureEmail = async (job) => {
    try {
      await api.post(`/contract-jobs/${job.job_id}/resend-signature-email`);
      toast.success("Unterschrift-Link wurde erneut an den Kunden geschickt");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const previewPdf = async (jobId) => {
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/contract-jobs/${jobId}/preview.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) win.location.href = url;
    } catch (e) {
      if (win) win.close();
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadSignedPdf = async (job) => {
    try {
      const res = await api.get(`/contract-jobs/${job.job_id}/signed.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vertrag_${job.client_name || job.client_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const regeneratePdf = async (job) => {
    try {
      await api.post(`/contract-jobs/${job.job_id}/regenerate-pdf`);
      toast.success("PDF wurde neu erzeugt");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const checkSignature = async (job) => {
    try {
      const { data } = await api.get(`/contract-jobs/${job.job_id}/signature-check`);
      const c = data.client_signature;
      const detail = c.present
        ? `Gespeichert: ${c.size || "?"} Pixel, ${c.ink_percent}% Strichanteil${c.error ? ` · Fehler: ${c.error}` : ""}`
        : "Kein Unterschriftsbild im Datensatz";
      const ok = c.present && c.readable && c.ink_percent >= 0.05;
      toast[ok ? "success" : "error"](`${data.verdict}\n${detail}`, { duration: 14000 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const requestNewSignature = async (job) => {
    if (!window.confirm(`Vertrag von "${job.client_name}" erneut zur Unterschrift senden?\n\nDer Kunde bekommt sofort eine E-Mail mit einem neuen Unterschrift-Link und wird danach automatisch erinnert, solange nicht unterschrieben wurde.`)) return;
    try {
      const { data } = await api.post(`/contract-jobs/${job.job_id}/request-new-signature`);
      await navigator.clipboard.writeText(data.sign_link).catch(() => {});
      toast.success(
        data.email_sent
          ? "E-Mail mit neuem Unterschrift-Link ist raus · Link zusätzlich in die Zwischenablage kopiert"
          : "Neuer Link erzeugt und in die Zwischenablage kopiert — die E-Mail konnte nicht zugestellt werden",
        { duration: 12000 },
      );
      setTab("sent_for_signature");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const exportSignedZip = async () => {
    try {
      const res = await api.get("/contract-jobs/export/signed", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vertraege_signiert_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("ZIP-Export wurde heruntergeladen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const filtered = jobs.filter((j) => j.status === tab && (j.customer_type || "avgs") === customerType);
  const manualOptions = customerType === "weiterbildung" ? wbParticipants : clients;

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1300px] mx-auto" data-testid="vertraege-page">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5">
          <FileSignature size={11} strokeWidth={1.5} /> Vertrags-Automatisierung
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">Verträge</h1>
        <p className="text-sm text-slate-600 mt-2">Wird automatisch erzeugt, sobald eine mit einem Kunden bzw. Weiterbildungs-Teilnehmer verknüpfte Karte auf dem jeweiligen Board nach „Vertrag erstellen“ verschoben wird · nach Freigabe geht der Unterschrift-Link per E-Mail raus.</p>
      </div>

      {/* Haupt-Tabs */}
      <div className="flex gap-1 mb-8" data-testid="vertrag-main-tabs">
        <button onClick={() => setMainTab("workflow")} data-testid="vertrag-main-tab-workflow"
          className={`px-4 py-2.5 text-xs font-bold uppercase tracking-overline flex items-center gap-1.5 ${mainTab === "workflow" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
          <ListTree size={12} strokeWidth={1.5} /> Vertrags-Workflow
        </button>
        <button onClick={() => setMainTab("katalog")} data-testid="vertrag-main-tab-katalog"
          className={`px-4 py-2.5 text-xs font-bold uppercase tracking-overline flex items-center gap-1.5 ${mainTab === "katalog" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
          <LayoutGrid size={12} strokeWidth={1.5} /> Maßnahmen-Katalog
        </button>
      </div>

      {mainTab === "katalog" ? (
        <MassnahmenKatalog />
      ) : (
      <>
      {/* Kunden-Typ-Tabs */}
      <div className="flex gap-1 mb-6" data-testid="vertrag-customer-type-tabs">
        <button onClick={() => { setCustomerType("avgs"); setManualClientId(""); }} data-testid="vertrag-customer-type-avgs"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-overline border ${customerType === "avgs" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500 hover:bg-slate-100"}`}>
          AVGS-Verträge
        </button>
        <button onClick={() => { setCustomerType("weiterbildung"); setManualClientId(""); }} data-testid="vertrag-customer-type-weiterbildung"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-overline border ${customerType === "weiterbildung" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500 hover:bg-slate-100"}`}>
          Weiterbildungs-Verträge
        </button>
      </div>
      {/* Manuelle Auslösung */}
      <div className="border border-slate-200 bg-white p-6 mb-8" data-testid="vertrag-manual-trigger-card">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Manuell auslösen (Fallback ohne Board-Trigger)</div>
        <div className="flex gap-2 items-end flex-wrap">
          <select value={manualClientId} onChange={(e) => setManualClientId(e.target.value)}
            className="input-tech text-xs" data-testid="vertrag-manual-client-picker">
            <option value="">{customerType === "weiterbildung" ? "— Teilnehmer wählen —" : "— Kunde wählen —"}</option>
            {manualOptions.map((o) => <option key={o.client_id || o.participant_id} value={o.client_id || o.participant_id}>{o.name}</option>)}
          </select>
          <button onClick={manualTrigger} className="px-3 py-2 bg-slate-900 text-white text-xs font-medium hover:bg-black flex items-center gap-1.5" data-testid="vertrag-manual-trigger-btn">
            <Send size={12} strokeWidth={1.5} /> Vertrag erzeugen
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-slate-200 mb-6">
        <div className="flex gap-1" data-testid="vertrag-tabs">
          {Object.keys(STATUS_LABEL).map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`vertrag-tab-${k}`}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              {STATUS_LABEL[k]} ({jobs.filter((j) => j.status === k && (j.customer_type || "avgs") === customerType).length})
            </button>
          ))}
        </div>
        {tab === "signed" && jobs.filter((j) => j.status === "signed").length > 0 && (
          <button onClick={exportSignedZip} className="px-3 py-1.5 mb-2 text-xs font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1.5" data-testid="vertrag-export-zip-btn">
            <FileArchive size={12} strokeWidth={1.5} /> Alle Exportieren (ZIP)
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 text-center py-10">Lädt…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-400 italic text-center py-10" data-testid="vertrag-list-empty">Keine Einträge.</div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 bg-white" data-testid="vertrag-list">
          {filtered.map((j) => (
            <div key={j.job_id} className="p-5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm font-medium text-slate-900">{j.client_name}</div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">
                  {j.client_email} · erzeugt {new Date(j.created_at).toLocaleString("de-DE")}
                  {j.updated_at && new Date(j.updated_at) - new Date(j.created_at) > 60000 && j.status !== "signed" && (
                    <span className="text-teal-700" data-testid={`vertrag-updated-${j.job_id}`}> · Daten aktualisiert {new Date(j.updated_at).toLocaleString("de-DE")}</span>
                  )}
                </div>
                {j.status === "sent_for_signature" && (
                  <div className="text-xs text-slate-600 mt-1" data-testid={`vertrag-reminder-info-${j.job_id}`}>
                    {j.signature_retry_requested_at && (
                      <span className="text-red-700 font-medium">Erneut zur Unterschrift gesendet am {new Date(j.signature_retry_requested_at).toLocaleString("de-DE")} · </span>
                    )}
                    {j.reminder_count
                      ? `${j.reminder_count} Erinnerung${j.reminder_count > 1 ? "en" : ""} an den Kunden geschickt, zuletzt ${new Date(j.reminder_sent_at).toLocaleString("de-DE")} — weitere folgen täglich automatisch`
                      : "Noch nicht unterschrieben — der Kunde wird nach 24 Stunden automatisch erinnert, danach täglich"}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-overline px-2 py-1 ${STATUS_COLOR[j.status]}`}>{STATUS_LABEL[j.status]}</span>
                {j.status === "signed" && (j.has_client_signature === false || (j.client_signature_quality && j.client_signature_quality !== "ok")) && (
                  <span className="text-[10px] font-bold uppercase tracking-overline px-2 py-1 bg-red-50 text-red-700 border border-red-200" title={j.client_signature_quality === "empty" ? "Das Unterschriftsfeld wurde leer gespeichert – im PDF bleibt die Zeile leer. Bitte erneut unterschreiben lassen." : j.client_signature_quality === "unreadable" ? "Das gespeicherte Unterschriftsbild lässt sich nicht lesen. Bitte erneut unterschreiben lassen." : "Im Datensatz ist keine Kundenunterschrift gespeichert – das PDF bleibt an dieser Stelle leer. Bitte erneut unterschreiben lassen."} data-testid={`vertrag-no-signature-${j.job_id}`}>
                    <AlertTriangle size={10} strokeWidth={2} className="inline mr-1 -mt-0.5" />
                    {j.client_signature_quality === "empty" ? "Unterschrift nicht erkannt" : j.client_signature_quality === "unreadable" ? "Unterschrift unlesbar" : "Unterschrift fehlt"}
                  </span>
                )}
                {j.status === "signed" ? (
                  <>
                    <button onClick={() => downloadSignedPdf(j)} className="px-3 py-1.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid={`vertrag-download-${j.job_id}`}>
                      <Download size={12} strokeWidth={1.5} /> Herunterladen
                    </button>
                    <button onClick={() => checkSignature(j)} title="Prüfen, ob die Kundenunterschrift wirklich im Datensatz steckt und lesbar ist" className="px-3 py-1.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid={`vertrag-sigcheck-${j.job_id}`}>
                      <ShieldCheck size={12} strokeWidth={1.5} /> Unterschrift prüfen
                    </button>
                    <button onClick={() => regeneratePdf(j)} title="Falls die Unterschrift im PDF fehlt/leer ist: PDF aus den gespeicherten Daten neu erzeugen" className="px-3 py-1.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid={`vertrag-regenerate-${j.job_id}`}>
                      <RefreshCw size={12} strokeWidth={1.5} /> PDF neu erzeugen
                    </button>
                    <button onClick={() => requestNewSignature(j)} title="Schickt dem Kunden sofort eine E-Mail mit neuem Unterschrift-Link; danach wird automatisch erinnert, solange nicht unterschrieben wurde" className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${j.client_signature_quality && j.client_signature_quality !== "ok" ? "bg-red-600 text-white hover:bg-red-700" : "border border-slate-300 hover:bg-slate-100"}`} data-testid={`vertrag-request-new-signature-${j.job_id}`}>
                      <Send size={12} strokeWidth={1.5} /> Erneut unterschreiben lassen
                    </button>
                  </>
                ) : (
                  <button onClick={() => previewPdf(j.job_id)} className="px-3 py-1.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid={`vertrag-preview-${j.job_id}`}>
                    <Eye size={12} strokeWidth={1.5} /> Vorschau
                  </button>
                )}
                {j.status === "pending_approval" && (
                  <button onClick={() => setSelected(j)} className="px-3 py-1.5 text-xs font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1.5" data-testid={`vertrag-review-${j.job_id}`}>
                    <CheckCircle2 size={12} strokeWidth={1.5} /> Prüfen &amp; Freigeben
                  </button>
                )}
                {j.status === "sent_for_signature" && (
                  <>
                    <button onClick={() => resendSignatureEmail(j)} className="px-3 py-1.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid={`vertrag-resend-${j.job_id}`}>
                      <Send size={12} strokeWidth={1.5} /> Link erneut senden
                    </button>
                    <button onClick={() => resetToApproval(j)} className="px-3 py-1.5 text-xs font-medium border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5" data-testid={`vertrag-reset-${j.job_id}`}>
                      <RefreshCw size={12} strokeWidth={1.5} /> Zurück zur Freigabe
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <ApprovalModal job={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} />
      )}
      </>
      )}
    </div>
  );
}

function ApprovalModal({ job, onClose, onDone }) {
  const [form, setForm] = useState({ price_summe: "" });
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sigMissing, setSigMissing] = useState(false);
  const [me, setMe] = useState(null);
  const [redraw, setRedraw] = useState(false);
  useEffect(() => { api.get("/contract-jobs/signers/me").then((r) => setMe(r.data)).catch(() => setMe({ is_signer: false, signers: [] })); }, []);
  const useSaved = me?.has_signature && !redraw;

  const approve = async () => {
    if (!useSaved && !signature) {
      setSigMissing(true);
      toast.error("Bitte zuerst im Feld unten unterschreiben, dann Freigeben & an Kunde senden klicken");
      return;
    }
    setSigMissing(false);
    setSaving(true);
    try {
      await api.post(`/contract-jobs/${job.job_id}/approve`, { ...form, staff_signature: useSaved ? null : signature, save_signature: !useSaved });
      toast.success("Vertrag freigegeben — Kunde wurde per E-Mail informiert");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const reject = async () => {
    if (!window.confirm("Vertrag wirklich ablehnen?")) return;
    try { await api.post(`/contract-jobs/${job.job_id}/reject`, {}); toast.success("Vertrag abgelehnt"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="vertrag-approval-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Vertrag prüfen &amp; freigeben</div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">{job.client_name}</div>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="text-xs text-slate-500">Alle Vertragsangaben stammen aus der Kunden-Akte. Optional kannst du die Gesamtsumme hier überschreiben, falls keine Modul-Preise gepflegt wurden:</div>
          <input placeholder="Gesamtpreis (SUMME) — optional" value={form.price_summe} onChange={(e) => setForm({ ...form, price_summe: e.target.value })}
            className="input-tech text-xs w-full" data-testid="approval-price-summe" />
          {me && !me.is_signer && (
            <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="approval-not-signer">
              Nur {me.signers?.length ? me.signers.join(" oder ") : "Christoph Tull oder Nicole Hudl"} dürfen für den Bildungsträger unterschreiben. Sie wurden per Glocke und E-Mail informiert.
            </div>
          )}
          {me?.is_signer && useSaved && (
            <div className="border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-center justify-between gap-3" data-testid="approval-saved-signature">
              <span>Deine hinterlegte Unterschrift wird beim Freigeben automatisch eingesetzt.</span>
              <button onClick={() => setRedraw(true)} className="underline shrink-0" data-testid="approval-redraw-btn">Neu zeichnen</button>
            </div>
          )}
          {me?.is_signer && !useSaved && (
            <div>
              <label className={`text-xs font-bold uppercase tracking-overline block mb-2 ${sigMissing ? "text-red-600" : "text-slate-500"}`}>
                Unterschrift Bildungsträger — einmal unterschreiben, wird für künftige Verträge gespeichert *
              </label>
              <div className={sigMissing ? "ring-2 ring-red-400" : ""}>
                <SignaturePad value={signature} onChange={(v) => { setSignature(v); if (v) setSigMissing(false); }} testId="approval-signature-pad" />
              </div>
              {sigMissing && <div className="text-[11px] text-red-600 mt-1" data-testid="approval-signature-missing-hint">Bitte hier oben unterschreiben, bevor du freigibst.</div>}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-2 justify-between">
          <button onClick={reject} className="px-4 py-2.5 text-xs font-medium border border-red-300 text-red-600 hover:bg-red-50 flex items-center gap-1.5" data-testid="approval-reject-btn">
            <XCircle size={12} strokeWidth={1.5} /> Ablehnen
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2.5 text-xs font-medium border border-slate-300 hover:bg-slate-100">Abbrechen</button>
            <button onClick={approve} disabled={saving || !me?.is_signer} className="px-4 py-2.5 text-xs font-medium bg-slate-900 text-white hover:bg-black disabled:opacity-50 flex items-center gap-1.5" data-testid="approval-approve-btn">
              {saving ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} strokeWidth={1.5} />} Freigeben &amp; an Kunde senden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
